import { Select, Tooltip } from "antd";
import { ICellRendererParams } from "ag-grid-community";
import { compact, map, uniq } from "underscore";
import { useEffect, useRef, useState } from "react";
import { showToast } from "@/ts/modals";
import { updateProductMapping } from "@/ts/utils";
import { useSelector } from "react-redux";
import { CustomTagProps, TProductMappingData, TProductMappingUpdateConfig, TUpdateConfigBulkProductMapping } from "@/ts/common-types";

const UniqueSkuIdCellRenderer = (params: ICellRendererParams) => {
    const productMappingData = useSelector(
        (state: TProductMappingData) => state.productMappingData.value
    );

    const [optionsList] = useState<string[]>(
        uniq(
            compact(
                map(productMappingData, (data) => data.sku_identifier)
            ) as string[]
        )
    );

    const initialSelectedValue = params.value ? [params.value] : [];

    const [selectedValue, setSelectedValue] = useState(initialSelectedValue);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const changeOccurred = useRef(false);

    const customTagRender = (props: CustomTagProps) => {
        const { label } = props;
        return (
            <Tooltip title={label}>
                <span
                    style={{
                        marginRight: "3px",
                        padding: "2px 6px",
                        backgroundColor: "#EBEBEB",
                        borderRadius: "5px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        width: "auto",
                        maxWidth: "22ch",
                        display: "inline-block",
                        verticalAlign: "middle"
                    }}
                >
                    {label}
                </span>
            </Tooltip>
        );
    };

    const processProductMappingUpdate = async (selectedValue: string[]) => {
        try {
            const updateConfig: TUpdateConfigBulkProductMapping = {
                sku_channel: [
                    {
                        channel: params.data.channel_id as number,
                        sku: params.data.sku as string | number,
                    },
                ],
                update_params: {
                    crossChannelProductId: selectedValue.length
                        ? selectedValue[0]
                        : "",
                },
            };

            return await updateProductMapping(updateConfig);
        } catch (err: any) {
            console.error(err.message);
        }
    };

    useEffect(() => {
        if (!isDropdownOpen && changeOccurred.current) {
            processProductMappingUpdate(selectedValue)
                .then((res) => {
                    if (res.status) {
                        params.api.getRowNode(params.node.id).setData({
                            ...params.data,
                            [params.colDef.field]: selectedValue,
                        });

                        showToast(
                            "success",
                            `${params.colDef.headerName} updated successfully`
                        );
                    }
                })
                .catch((err) => {
                    showToast(
                        "danger",
                        `Failed to update ${params.colDef.headerName}`
                    );
                    console.error(err);
                });

            changeOccurred.current = false;
        }
    }, [isDropdownOpen, selectedValue]);

    const handleDropdownClose = (open: boolean) =>
        setIsDropdownOpen((prev) => !prev);

    const handleChange = (value: string[]) => {
        changeOccurred.current = true;

        if (value.length > 1) {
            showToast("danger", "Only one product id can be set to a sku");
            return;
        }

        if (value.length === 0) {
            setSelectedValue([]);
            return;
        }
        setSelectedValue([value[0]]);
    };

    return (
        <>
            {params.node.rowPinned ? null : (
                <Select
                    mode={"tags"}
                    style={{ width: "100%" }}
                    value={selectedValue}
                    placeholder={"Select ID"}
                    maxTagCount={1}
                    options={map(optionsList, (option) => ({
                        value: option,
                        label: option,
                    }))}
                    onChange={handleChange}
                    onDropdownVisibleChange={handleDropdownClose}
                    tagRender={customTagRender}
                />
            )}
        </>
    );
};

export default UniqueSkuIdCellRenderer;
